import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, Briefcase, Upload, FileText, Trash2, ExternalLink, CheckCircle2, Send, FlaskConical, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = [
  { key: "job", label: "Puesto al que optas", type: "job", hint: "Selecciona el puesto (job description) o describe uno." },
  { key: "tools_used", label: "Herramientas IA que usas", hint: "ChatGPT, Copilot, Claude, Perplexity, agentes, etc.", placeholder: "Describe las herramientas de IA que utilizas habitualmente..." },
  { key: "agents_configured", label: "Agentes configurados", hint: "Agentes propios, custom GPTs, bots, asistentes que hayas configurado.", placeholder: "¿Qué agentes tienes configurados y para qué?" },
  { key: "tasks_delegated", label: "Tareas que delegas", hint: "Qué partes de tu trabajo delegas a la IA.", placeholder: "Research, redacción de emails, análisis, reporting..." },
  { key: "workflows_built", label: "Workflows construidos", hint: "Procesos encadenados que hayas diseñado de principio a fin.", placeholder: "Describe un workflow que hayas construido..." },
  { key: "automations", label: "Automatizaciones", hint: "Tareas recurrentes que has automatizado (Zapier, Make, n8n, APIs...).", placeholder: "¿Qué has automatizado y con qué herramientas?" },
  { key: "results_obtained", label: "Resultados obtenidos", hint: "Ahorro de tiempo, incremento de output, mejoras medibles.", placeholder: "Ej. reduje de 4h a 30min la preparación de propuestas..." },
  { key: "limitations_known", label: "Limitaciones que conoces", hint: "Qué limitaciones o riesgos identificas en tu uso de IA.", placeholder: "¿Qué limitaciones conoces de tus agentes/herramientas?" },
  { key: "controls_applied", label: "Controles que aplicas", hint: "Cómo verificas outputs, proteges datos y evitas errores.", placeholder: "¿Qué controles de calidad y seguridad aplicas?" },
];

const STAGES = ["Cuestionario", "Preguntas", "Evidencias", "Prueba práctica", "Enviar"];

export default function CandidateAssessment() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: async () => (await api.get("/jobs")).data });

  const [stageIdx, setStageIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState("");
  const [draft, setDraft] = useState(null);

  // Stage 0: questionnaire
  const [step, setStep] = useState(0);
  const [jobMode, setJobMode] = useState("select");
  const [data, setData] = useState({
    job_id: "", target_role: "", tools_used: "", agents_configured: "", tasks_delegated: "",
    workflows_built: "", automations: "", results_obtained: "", limitations_known: "", controls_applied: "",
  });
  const current = STEPS[step];
  const canNext = current.type === "job" ? data.target_role.trim().length > 0 : data[current.key].trim().length > 0;
  const update = (v) => setData((d) => ({ ...d, [current.key]: v }));
  const selectJob = (val) => {
    if (val === "__other__") { setJobMode("other"); setData((d) => ({ ...d, job_id: "", target_role: "" })); }
    else { const job = jobs.find((j) => j.id === val); setJobMode("select"); setData((d) => ({ ...d, job_id: val, target_role: job?.title || "" })); }
  };

  // Stage 1: adaptive answers
  const [adaptiveAnswers, setAdaptiveAnswers] = useState({});
  // Stage 3: practical
  const [practicalAnswer, setPracticalAnswer] = useState("");
  const fileRef = useRef(null);
  const fileUrl = (p) => `${API}/files/${p}?auth=${encodeURIComponent(localStorage.getItem("token") || "")}`;

  const refreshDraft = async () => {
    const { data: d } = await api.get(`/assessments/${draft.id}`);
    setDraft(d);
    return d;
  };

  const finishQuestionnaire = async () => {
    setBusy(true); setBusyMsg("Preparando tus preguntas de seguimiento…");
    try {
      const payload = { ...data }; delete payload.job;
      const { data: res } = await api.post("/assessments/draft", payload);
      setDraft(res);
      setAdaptiveAnswers(Object.fromEntries((res.adaptive_questions || []).map((q, i) => [i, ""])));
      setStageIdx(1);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const saveAdaptiveAndNext = async () => {
    setBusy(true); setBusyMsg("Guardando…");
    try {
      const answers = (draft.adaptive_questions || []).map((q, i) => ({ question: q.question, answer: adaptiveAnswers[i] || "" }));
      await api.patch(`/assessments/${draft.id}/adaptive-answers`, { answers });
      setStageIdx(2);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true); setBusyMsg(`Analizando ${file.name}…`);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/assessments/${draft.id}/evidence`, fd);
      await refreshDraft();
      toast.success("Evidencia añadida");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteEvidence = async (eid) => {
    await api.delete(`/assessments/${draft.id}/evidence/${eid}`); await refreshDraft();
  };

  const genCase = async () => {
    setBusy(true); setBusyMsg("Generando tu prueba práctica…");
    try { await api.post(`/assessments/${draft.id}/practical-case`); await refreshDraft(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const savePracticalAndNext = async () => {
    setBusy(true); setBusyMsg("Guardando…");
    try {
      if (draft.practical_case && practicalAnswer.trim().length >= 20) {
        await api.patch(`/assessments/${draft.id}/practical-answer`, { response_text: practicalAnswer });
      }
      setStageIdx(4);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitFinal = async () => {
    setBusy(true); setBusyMsg("Enviando tu evaluación al Deputy…");
    try {
      await api.post(`/assessments/${draft.id}/submit`);
      await qc.invalidateQueries({ queryKey: ["my-assessments"] });
      toast.success("¡Evaluación enviada! El Deputy la está procesando; su estado aparecerá en Mis evaluaciones.");
      navigate("/app/portal");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); setBusy(false); }
  };

  if (busy) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center">
        <div className="relative">
          <Loader2 className="text-primary animate-spin" size={48} strokeWidth={1.5} />
          <Sparkles className="absolute -top-1 -right-1 text-amber-400" size={18} />
        </div>
        <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight">{busyMsg || "Procesando…"}</h2>
        <p className="mt-2 text-slate-400 max-w-md">Esto puede tardar unos segundos.</p>
      </div>
    );
  }

  const Stepper = () => (
    <div className="flex items-center gap-2 mb-8 flex-wrap" data-testid="journey-stepper">
      {STAGES.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs ${i === stageIdx ? "bg-primary/15 text-primary" : i < stageIdx ? "text-emerald-400" : "text-slate-500"}`}>
            {i < stageIdx ? <CheckCircle2 size={14} /> : <span className="font-mono">{i + 1}</span>}
            {s}
          </div>
          {i < STAGES.length - 1 && <span className="text-slate-700">›</span>}
        </div>
      ))}
    </div>
  );

  // ---- Stage 0: questionnaire ----
  if (stageIdx === 0) {
    const isLast = step === STEPS.length - 1;
    return (
      <div className="max-w-2xl">
        <Stepper />
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.2em] text-primary">Cuestionario del candidato</span>
          <span className="font-mono text-sm text-slate-400">{step + 1} / {STEPS.length}</span>
        </div>
        <div className="h-1 w-full rounded-full bg-slate-800 mb-8">
          <div className="h-1 rounded-full bg-primary transition-all duration-300" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={current.key} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <Label className="font-heading text-2xl font-bold tracking-tight text-slate-100">{current.label}</Label>
            <p className="text-sm text-slate-400 mt-1.5 mb-5">{current.hint}</p>
            {current.type === "job" ? (
              <div className="space-y-4">
                <Select value={data.job_id || (jobMode === "other" ? "__other__" : "")} onValueChange={selectJob}>
                  <SelectTrigger data-testid="job-select" className="bg-slate-950 border-slate-800 focus:border-primary text-base py-6">
                    <SelectValue placeholder="Selecciona un puesto…" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((j) => (<SelectItem key={j.id} value={j.id} data-testid={`job-option-${j.id}`}>{j.title}</SelectItem>))}
                    <SelectItem value="__other__">Otro (describir)</SelectItem>
                  </SelectContent>
                </Select>
                {jobMode === "other" && (
                  <Input autoFocus data-testid="q-target_role" value={data.target_role} onChange={(e) => setData((d) => ({ ...d, target_role: e.target.value }))} placeholder="Ej. Director Comercial" className="bg-slate-950 border-slate-800 focus:border-primary text-base py-6" />
                )}
                {jobMode === "select" && data.job_id && (
                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 flex items-start gap-3">
                    <Briefcase size={16} className="text-primary mt-0.5" strokeWidth={1.5} />
                    <p className="text-sm text-slate-400 whitespace-pre-wrap max-h-48 overflow-y-auto">{jobs.find((j) => j.id === data.job_id)?.description?.slice(0, 600)}…</p>
                  </div>
                )}
              </div>
            ) : (
              <Textarea autoFocus data-testid={`q-${current.key}`} value={data[current.key]} onChange={(e) => update(e.target.value)} placeholder={current.placeholder} rows={6} className="bg-slate-950 border-slate-800 focus:border-primary text-base" />
            )}
          </motion.div>
        </AnimatePresence>
        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost" data-testid="wizard-back-btn" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="gap-2 text-slate-400 hover:text-slate-100 disabled:opacity-40"><ArrowLeft size={18} /> Atrás</Button>
          {isLast ? (
            <Button data-testid="wizard-next-btn" disabled={!canNext} onClick={finishQuestionnaire} className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">Continuar <ArrowRight size={18} /></Button>
          ) : (
            <Button data-testid="wizard-next-btn" disabled={!canNext} onClick={() => setStep((s) => s + 1)} className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">Siguiente <ArrowRight size={18} /></Button>
          )}
        </div>
      </div>
    );
  }

  // ---- Stage 1: adaptive questions ----
  if (stageIdx === 1) {
    const qs = draft.adaptive_questions || [];
    return (
      <div className="max-w-2xl">
        <Stepper />
        <h2 className="font-heading text-2xl font-bold tracking-tight">Preguntas de seguimiento</h2>
        <p className="text-sm text-slate-400 mt-1.5 mb-6">El Deputy ha preparado estas preguntas a partir de tus respuestas. Responde para dar más contexto.</p>
        {qs.length === 0 && <p className="text-slate-500">No hay preguntas adicionales. Puedes continuar.</p>}
        <div className="space-y-5">
          {qs.map((q, i) => (
            <div key={i} data-testid={`adaptive-q-${i}`}>
              <Label className="text-slate-200">{i + 1}. {q.question}</Label>
              <Textarea data-testid={`adaptive-a-${i}`} value={adaptiveAnswers[i] || ""} onChange={(e) => setAdaptiveAnswers((a) => ({ ...a, [i]: e.target.value }))} rows={3} className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary" placeholder="Tu respuesta…" />
            </div>
          ))}
        </div>
        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost" onClick={() => setStageIdx(0)} className="gap-2 text-slate-400 hover:text-slate-100"><ArrowLeft size={18} /> Atrás</Button>
          <Button data-testid="adaptive-next-btn" onClick={saveAdaptiveAndNext} className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">Continuar <ArrowRight size={18} /></Button>
        </div>
      </div>
    );
  }

  // ---- Stage 2: evidence ----
  if (stageIdx === 2) {
    const suggested = draft.suggested_evidence || [];
    const evidences = draft.evidences || [];
    return (
      <div className="max-w-2xl">
        <Stepper />
        <h2 className="font-heading text-2xl font-bold tracking-tight">Aporta evidencias</h2>
        <p className="text-sm text-slate-400 mt-1.5 mb-2">Sube pruebas reales de lo que declaras (capturas, exportaciones de workflows, PDFs, documentos). Son sugerencias: sube las que consideres.</p>
        <p className="text-xs text-amber-400/80 mb-5">No subas contraseñas, claves ni datos confidenciales de terceros.</p>

        {suggested.length > 0 && (
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 mb-5">
            <div className="flex items-center gap-2 text-primary mb-3"><Lightbulb size={16} /> <span className="text-sm font-medium">Evidencias sugeridas</span></div>
            <ul className="space-y-2">
              {suggested.map((s, i) => (
                <li key={i} className="text-sm text-slate-300" data-testid={`suggested-evidence-${i}`}>
                  <span className="text-slate-100">• {s.title}</span>{s.why && <span className="text-slate-500"> — {s.why}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <input ref={fileRef} type="file" onChange={handleUpload} className="hidden" accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.md,.txt,.json,.yaml,.yml,.csv" data-testid="evidence-file-input" />
        <Button data-testid="evidence-upload-btn" onClick={() => fileRef.current?.click()} className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400 mb-4"><Upload size={16} /> Subir evidencia</Button>

        <div className="space-y-2">
          {evidences.map((e) => (
            <div key={e.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 px-4 py-3" data-testid={`uploaded-evidence-${e.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={15} className="text-primary shrink-0" />
                <span className="text-sm text-slate-200 truncate">{e.filename}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <a href={fileUrl(e.storage_path)} target="_blank" rel="noreferrer" className="text-primary hover:text-cyan-300" title="Abrir"><ExternalLink size={15} /></a>
                <button onClick={() => deleteEvidence(e.id)} className="text-slate-500 hover:text-red-400"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost" onClick={() => setStageIdx(1)} className="gap-2 text-slate-400 hover:text-slate-100"><ArrowLeft size={18} /> Atrás</Button>
          <Button data-testid="evidence-next-btn" onClick={() => setStageIdx(3)} className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">Continuar <ArrowRight size={18} /></Button>
        </div>
      </div>
    );
  }

  // ---- Stage 3: practical ----
  if (stageIdx === 3) {
    const pc = draft.practical_case;
    return (
      <div className="max-w-2xl">
        <Stepper />
        <h2 className="font-heading text-2xl font-bold tracking-tight">Prueba práctica</h2>
        <p className="text-sm text-slate-400 mt-1.5 mb-5">Un mini-caso para demostrar tu capacidad de orquestación. Opcional pero recomendado.</p>
        {!pc ? (
          <Button data-testid="gen-case-btn" onClick={genCase} className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400"><FlaskConical size={16} /> Generar mi prueba</Button>
        ) : (
          <>
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
              <h3 className="font-heading text-lg font-medium tracking-tight text-slate-100">{pc.title}</h3>
              <p className="text-sm text-slate-300 mt-2">{pc.scenario}</p>
              <p className="text-sm text-slate-200 mt-2"><span className="text-primary">Objetivo: </span>{pc.objective}</p>
              {pc.tasks?.length > 0 && (
                <ul className="mt-3 space-y-1 text-sm text-slate-400 list-disc pl-5">{pc.tasks.map((t, i) => <li key={i}>{t}</li>)}</ul>
              )}
            </div>
            <Textarea data-testid="practical-answer" value={practicalAnswer} onChange={(e) => setPracticalAnswer(e.target.value)} rows={8} className="mt-4 bg-slate-950 border-slate-800 focus:border-primary" placeholder="Describe tu solución: workflow, agentes, arquitectura, controles, comparación manual vs con agentes…" />
          </>
        )}
        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost" onClick={() => setStageIdx(2)} className="gap-2 text-slate-400 hover:text-slate-100"><ArrowLeft size={18} /> Atrás</Button>
          <Button data-testid="practical-next-btn" onClick={savePracticalAndNext} className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">Continuar <ArrowRight size={18} /></Button>
        </div>
      </div>
    );
  }

  // ---- Stage 4: review & submit ----
  const evCount = (draft.evidences || []).length;
  const answered = (draft.adaptive_questions || []).filter((q, i) => (adaptiveAnswers[i] || "").trim()).length;
  return (
    <div className="max-w-2xl">
      <Stepper />
      <h2 className="font-heading text-2xl font-bold tracking-tight">Revisa y envía</h2>
      <p className="text-sm text-slate-400 mt-1.5 mb-6">Al enviar, el Deputy analizará todo y el consultor recibirá tu evaluación. No verás el resultado detallado: es para el consultor.</p>
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 space-y-3">
        <div className="flex justify-between text-sm"><span className="text-slate-400">Puesto</span><span className="text-slate-100">{draft.target_role}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-400">Cuestionario</span><span className="text-emerald-400">Completado</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-400">Preguntas de seguimiento</span><span className="text-slate-100">{answered} / {(draft.adaptive_questions || []).length} respondidas</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-400">Evidencias aportadas</span><span className="text-slate-100">{evCount}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-400">Prueba práctica</span><span className="text-slate-100">{draft.practical_case?.submission ? "Respondida" : "No realizada"}</span></div>
      </div>
      <div className="mt-8 flex items-center justify-between">
        <Button variant="ghost" onClick={() => setStageIdx(3)} className="gap-2 text-slate-400 hover:text-slate-100"><ArrowLeft size={18} /> Atrás</Button>
        <Button data-testid="submit-final-btn" onClick={submitFinal} className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">Enviar evaluación <Send size={18} /></Button>
      </div>
    </div>
  );
}
